import { AddOutlined, DeleteOutline, QrCode } from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Stack,
} from '@mui/material'
import type {
  GetSchedulesRequest,
  Schedule,
} from '@ulysses-inc/harami_api_client'
import { type FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import Loading from 'src/components/loading/Loading'
import schedulesOperations from 'src/state/ducks/schedules/operations'
import type { GetSchedulesFilter } from 'src/state/ducks/schedules/types'
import type { RootState } from 'src/state/store'
import ScheduleListContent from './ScheduleListContent'
import SchedulesFilterDropDown from './SchedulesFilterDropDown'

const ScheduleList: FC = () => {
  const schedules = useSelector(
    (state: RootState) => state.schedulesState.schedules.schedules,
  )
  const isLoading = useSelector(
    (state: RootState) => state.schedulesState.schedules.isLoading,
  )
  const request = useSelector(
    (state: RootState) => state.schedulesState.schedules.request,
  )
  const filter = useSelector(
    (state: RootState) => state.schedulesState.schedules.filter,
  )
  const history = useHistory()
  const dispatch = useDispatch()
  const deleteSchedules = (
    scheduleUUIDs: string[],
    request: GetSchedulesRequest,
    filter?: GetSchedulesFilter,
  ) => {
    schedulesOperations.deleteSchedules(
      dispatch,
      scheduleUUIDs,
      request,
      filter,
    )
  }
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [flowNames, setFlowNames] = useState<string[]>([])

  const goAddSchedule = () => {
    history.push('/schedules/add')
  }
  const downloadScheduleQrCodes = (scheduleUUIDs: string[]) =>
    schedulesOperations.downloadScheduleQrCodes(dispatch, scheduleUUIDs)

  const chooseScheduleUUIDsState = useState<string[]>([])
  const [chooseScheduleUUIDs, setChooseScheduleUUIDs] = chooseScheduleUUIDsState

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
    setChooseScheduleUUIDs([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedules])

  const getSchedulesHandler = (f?: GetSchedulesFilter) =>
    schedulesOperations.getSchedules(dispatch, request, f)
  const onDownloadListButtonClick = () => {
    downloadScheduleQrCodes(chooseScheduleUUIDs)
  }
  const onBulkDeleteButtonClick = () => {
    const flowUUIDs: { [uuid: string]: string } = {}
    schedules
      ?.filter(
        (schedule: Schedule) =>
          schedule.uuid &&
          chooseScheduleUUIDs.includes(schedule.uuid) &&
          schedule.excelConversionFlows?.length,
      )
      .forEach(schedule => {
        schedule.excelConversionFlows?.forEach(flow => {
          if (!flow.flowUUID) return
          flowUUIDs[flow.flowUUID] = flow.flowName || ''
        })
      })

    setFlowNames(Object.values(flowUUIDs))
    setOpenDeleteDialog(true)
  }

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        p={2}
      >
        <SchedulesFilterDropDown getSchedules={getSchedulesHandler} />
        <Stack direction="row" spacing={2}>
          <Button
            onClick={() => goAddSchedule()}
            variant="contained"
            color="primary"
            startIcon={<AddOutlined />}
          >
            スケジュールを追加
          </Button>
          <Button
            onClick={onDownloadListButtonClick}
            disabled={chooseScheduleUUIDs.length === 0}
            variant="outlined"
            color="primary"
            startIcon={<QrCode />}
          >
            QRコード一括ダウンロード
          </Button>
          <Button
            onClick={onBulkDeleteButtonClick}
            disabled={chooseScheduleUUIDs.length === 0}
            variant="outlined"
            color="error"
            startIcon={<DeleteOutline />}
          >
            一括削除
          </Button>
        </Stack>
      </Stack>
      {isLoading ? (
        <Loading />
      ) : (
        <ScheduleListContent
          chooseScheduleUUIDsState={chooseScheduleUUIDsState}
        />
      )}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>
          {flowNames.length > 0
            ? '以下のExcel変換設定に紐づいています。一括削除しますがよろしいですか?'
            : '選択中のスケジュールを一括削除しますがよろしいですか?'}
        </DialogTitle>
        {flowNames.length > 0 && (
          <DialogContent>
            <List>
              {flowNames.map(name => (
                <ListItem key={name}>{name}</ListItem>
              ))}
            </List>
          </DialogContent>
        )}
        <DialogActions>
          <Button variant="outlined" onClick={() => setOpenDeleteDialog(false)}>
            キャンセル
          </Button>
          <Button
            onClick={e => {
              e.stopPropagation()
              e.preventDefault()
              deleteSchedules(chooseScheduleUUIDs, request, filter)
              setOpenDeleteDialog(false)
            }}
            color="error"
            variant="contained"
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ScheduleList
